/**
 * AI Insights page (Phase 11E).
 *
 * Three panels:
 * - Recommended Bullish — top symbols by signal × inverse-vol
 * - Signal Drilldown — score, label, model_version, feature breakdown
 *   for the symbol picked from the recommendations list
 * - Anomaly Tape — recent flash-spike events on the picked symbol
 *
 * Honest framing: the v1 model is a 5-feature logistic regression
 * trained on synthesized data (see src/trader/ml/train.py). Scores
 * are a directional confidence indicator, not a trading
 * recommendation.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ml, type MlAnomaly, type MlRecommendation, type MlSignal } from "@/api/ml";


export function InsightsPage() {
  const [direction, setDirection] = useState<"bullish" | "bearish" | "any">("bullish");
  const [picked, setPicked] = useState<string | null>(null);

  const recsQ = useQuery({
    queryKey: ["ml-recs", direction],
    queryFn: () => ml.recommended(direction, 10),
    staleTime: 30_000,
  });

  const sigQ = useQuery({
    queryKey: ["ml-signal", picked],
    queryFn: () => picked ? ml.signal(picked) : Promise.resolve(null),
    enabled: !!picked,
    staleTime: 30_000,
  });

  const anomQ = useQuery({
    queryKey: ["ml-anomaly", picked],
    queryFn: () => picked ? ml.anomaly(picked) : Promise.resolve(null),
    enabled: !!picked,
    refetchInterval: 30_000,
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="border-b border-slate-800 px-6 py-6">
        <p className="text-xs uppercase tracking-widest text-slate-500">
          Phase 11E · v1 signal model
        </p>
        <h1 className="mt-1 text-2xl font-semibold">AI Insights</h1>
        <p className="mt-2 max-w-xl text-sm text-slate-400">
          5-feature logistic-regression model trained on TA signals (RSI / MACD / Bollinger
          position / ADX / volume z-score). Scores are a directional confidence indicator,
          not a trading recommendation. The Recommended panel weights by inverse volatility,
          so a stable mid-confidence idea beats a turbulent high-confidence one.
        </p>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8 grid gap-6 md:grid-cols-2">
        <Panel title="Recommended">
          <div className="mb-3 flex gap-2 text-xs">
            {(["bullish", "bearish", "any"] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDirection(d)}
                className={`rounded px-3 py-1 ${
                  direction === d
                    ? "bg-amber-400 text-black"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
          {recsQ.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {recsQ.error && (
            <p className="text-sm text-red-400">{String(recsQ.error)}</p>
          )}
          {recsQ.data && recsQ.data.length === 0 && (
            <p className="text-sm text-slate-500">No instruments with enough history.</p>
          )}
          <ul className="space-y-1">
            {recsQ.data?.map((r) => (
              <li key={r.symbol}>
                <button
                  onClick={() => setPicked(r.symbol)}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm ${
                    picked === r.symbol
                      ? "bg-slate-800"
                      : "hover:bg-slate-900"
                  }`}
                >
                  <span className="font-mono">{r.symbol}</span>
                  <span className="flex items-center gap-3">
                    <ScorePill rec={r} />
                    <span className="text-xs text-slate-500">vol {r.realized_vol_pct.toFixed(2)}%</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title={picked ? `Signal · ${picked}` : "Signal"}>
          {!picked && (
            <p className="text-sm text-slate-500">
              Pick a symbol from Recommended to see its score breakdown.
            </p>
          )}
          {picked && sigQ.isLoading && (
            <p className="text-sm text-slate-500">Loading…</p>
          )}
          {picked && sigQ.data && <SignalDetail sig={sigQ.data} />}

          {picked && (
            <div className="mt-6 border-t border-slate-800 pt-4">
              <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">
                Anomaly check
              </p>
              {anomQ.isLoading && <p className="text-sm text-slate-500">Checking…</p>}
              {anomQ.data ? <AnomalyDetail evt={anomQ.data} /> : (
                <p className="text-sm text-slate-500">No anomaly on the last bar.</p>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}


function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </p>
      {children}
    </div>
  );
}


function ScorePill({ rec }: { rec: MlRecommendation }) {
  const color =
    rec.label === "bullish" ? "bg-emerald-500/20 text-emerald-300"
    : rec.label === "bearish" ? "bg-red-500/20 text-red-300"
    : "bg-slate-700 text-slate-300";
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${color}`}>
      {rec.score} · {rec.label}
    </span>
  );
}


function SignalDetail({ sig }: { sig: MlSignal | null }) {
  if (!sig) return null;
  const featureRows = Object.entries(sig.features);
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-3xl font-semibold tabular-nums">{sig.score}</p>
        <p className={`text-sm uppercase ${
          sig.label === "bullish" ? "text-emerald-400"
          : sig.label === "bearish" ? "text-red-400"
          : "text-slate-400"
        }`}>{sig.label}</p>
      </div>
      <p className="text-xs text-slate-500">
        model {sig.model_version} {sig.cached ? "· cached" : ""}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 uppercase tracking-wider">
            <th className="text-left">Feature</th>
            <th className="text-right">Value</th>
          </tr>
        </thead>
        <tbody>
          {featureRows.map(([k, v]) => (
            <tr key={k} className="border-t border-slate-800">
              <td className="py-1.5">{k}</td>
              <td className="py-1.5 text-right font-mono">{v.toFixed(4)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AnomalyDetail({ evt }: { evt: MlAnomaly }) {
  const color =
    evt.severity === "critical" ? "text-red-400"
    : evt.severity === "alert" ? "text-amber-400"
    : "text-yellow-400";
  return (
    <div className="space-y-1 text-sm">
      <p className={`uppercase ${color}`}>{evt.severity}</p>
      <p className="text-slate-300">
        return <span className="font-mono">{evt.return_pct.toFixed(2)}%</span>
        {" "}@ <span className="font-mono">{evt.z_score.toFixed(2)}σ</span>
      </p>
      <p className="text-xs text-slate-500">{evt.timestamp}</p>
    </div>
  );
}
